import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, useTheme, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import Sidebar from "../../components/sidebar/Sidebar";
import PageContainer from "../../components/layout/PageContainer";

import AddIcon from "@mui/icons-material/Add";
import SyncIcon from "@mui/icons-material/Sync";
import CompanyTable from "../../components/table/CompanyTable";
import { useDispatch, useSelector } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import {
  useGetCompaniesQuery,
  useGetUserCompaniesQuery,
  useSyncAllBillingsFromHoldedMutation,
} from "../../store/apis/companyApi";
import { components } from "../../types/api";
import { selectUserDb } from "../../store/slices/session/sessionSelector";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";

type CompanyType = components["schemas"]["Company"];

const Company: React.FC = () => {
  const navigate = useNavigate();
  const collapsed = useSelector(selectSidebarCollapsed);
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userDb = useSelector(selectUserDb);

  const isAdmin = userDb?.role === "ADMIN";

  // Determine if user can create companies
  const canCreateCompany =
    userDb?.role &&
    ["ADMIN", "ACCOUNT", "STRATEGY", "PRICING"].includes(userDb.role);

  // Sincronización global de datos fiscales desde Holded (solo admin).
  const [syncAllBillings, { isLoading: isSyncingAll }] =
    useSyncAllBillingsFromHoldedMutation();

  const handleSyncAllBillings = async () => {
    try {
      const resp = await syncAllBillings().unwrap();
      const s = resp.data;
      dispatch(
        setToastMessage({
          message: s
            ? t("billing.syncAllSummary", {
                synced: s.synced,
                failed: s.failed,
                total: s.total,
              })
            : t("billing.syncSuccess"),
          severity: s && s.failed > 0 ? "warning" : "success",
          autoHideDuration: 6000,
        })
      );
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        t("billing.syncError");
      dispatch(setToastMessage({ message, severity: "error" }));
    }
  };

  // Determine if user can delete companies
  const canDeleteCompany =
    userDb?.role && ["ADMIN", "STRATEGY", "PRICING"].includes(userDb.role);

  // Determine if user can view all companies (Admin, Strategy, Pricing)
  const canViewAllCompanies =
    userDb?.role && ["ADMIN", "STRATEGY", "PRICING"].includes(userDb.role);

  // Determine if user can run bulk actions (assign users / payment terms)
  const canBulkManage =
    userDb?.role &&
    ["ADMIN", "STRATEGY", "PRICING", "ACCOUNT"].includes(userDb.role);

  // Use the appropriate query based on user role
  const {
    data: adminCompanyResponse,
    isLoading: isAdminLoading,
    error: adminError,
  } = useGetCompaniesQuery(undefined, { skip: !canViewAllCompanies });

  const {
    data: userCompanyResponse,
    isLoading: isUserLoading,
    error: userError,
  } = useGetUserCompaniesQuery(undefined, {
    skip: !!canViewAllCompanies,
  });

  useEffect(() => {
    const error = canViewAllCompanies ? adminError : userError;
    if (error) {
      dispatch(
        setToastMessage({
          message: t("company.errors.fetchError"),
          severity: "error",
        })
      );
    }
  }, [adminError, userError, canViewAllCompanies, dispatch, t]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />
      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
          display: "flex",
          flexDirection: "column",
          width: { xs: "calc(100% - 80px)", sm: "auto" },
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
          minHeight: "100vh",
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          <Box
            sx={{
              mt: 3,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Box sx={{ minWidth: 0 }} />
            {(canCreateCompany || isAdmin) && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  flexShrink: 0,
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  gap: 1.5,
                }}
              >
                {isAdmin && (
                  <Button
                    variant="outlined"
                    onClick={handleSyncAllBillings}
                    disabled={isSyncingAll}
                    startIcon={
                      isSyncingAll ? (
                        <CircularProgress size={18} />
                      ) : (
                        <SyncIcon />
                      )
                    }
                    sx={{
                      px: 3,
                      py: 1.5,
                      borderRadius: "50px",
                      textTransform: "none",
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      borderColor: theme.palette.grey[300],
                      color: theme.palette.primary.main,
                    }}
                  >
                    {t("billing.syncAll")}
                  </Button>
                )}
                {canCreateCompany && (
                  <Button
                    variant="contained"
                    onClick={() => navigate("/companies/new")}
                    startIcon={
                      <AddIcon
                        sx={{
                          fontSize: 24,
                          color: theme.palette.primary.contrastText,
                        }}
                      />
                    }
                    sx={{
                      px: 3,
                      py: 1.5,
                      backgroundColor: theme.palette.primary.main,
                      boxShadow: "0px 8px 8px rgba(0, 0, 0, 0.04)",
                      borderRadius: "50px",
                      textTransform: "none",
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.primary.contrastText,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    }}
                  >
                    {t("company.new")}
                  </Button>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 3 }}>
            {(canViewAllCompanies ? isAdminLoading : isUserLoading) ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <CompanyTable
                companiesData={
                  canViewAllCompanies
                    ? (adminCompanyResponse?.data ?? [])
                    : (userCompanyResponse?.data ?? [])
                }
                maxHeight="none"
                isAdmin={!!canViewAllCompanies}
                canDeleteCompany={canDeleteCompany}
                canBulkManage={!!canBulkManage}
                onView={(company: CompanyType) =>
                  navigate(`/companies/${company.id}`)
                }
              />
            )}
          </Box>
        </PageContainer>
      </Box>
    </Box>
  );
};

export default Company;
